import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { CreatorResponseDto } from '../../../../common/general-types/creators.dto';
import { CreateRecruitGroupDto, RecruitGroupResponseDto, RecruitGroupTypesEnum } from '../../../../common/general-types/recruit-groups.dto';
import { FormField, form, minLength, required } from '@angular/forms/signals';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { HasRoleDirectiveDirective } from '../../../../common/directives/has-role/has-role-directive.directive';
import { RecruitGroupService } from '../../../../services/recruit-group/recruit-group.service';
import { FirebaseService } from '../../../../common/firebase/firebase.service';
import { ToastNotificationService } from '../../../../atoms/toast-notification/toast-notification.service';
import { AlertService } from '../../../../atoms/alert/alert.service';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ViewportOverlayDirective } from '../../../../common/directives/viewport-overlay/viewport-overlay.directive';

type RecruitGroupPrivacy = 'user' | 'brand' | 'global';

@Component({
  selector: 'ambala-groups',
  imports: [FormField, HasRoleDirectiveDirective],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './ambala-groups.component.html',
  styleUrl: './ambala-groups.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AmbalaGroupsComponent implements OnInit, OnDestroy {
  readonly creator = input.required<CreatorResponseDto>();
  readonly closed = output<void>();

  readonly groupsLoadState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  readonly updatingGroup = signal(false);
  readonly viewMode = signal<'view' | 'addGroup'>('view');
  readonly submitInProgress = signal(false);
  private readonly membershipRevision = signal(0);
  private readonly subscriptionList = new Subscription();

  readonly recruitGroupModel = signal<{
    groupName: string;
    groupDescription: string;
    privacy: RecruitGroupPrivacy;
  }>({
    groupName: '',
    groupDescription: '',
    privacy: 'user',
  });
  readonly recruitGroupForm = form(this.recruitGroupModel, (schemaPath) => {
    required(schemaPath.groupName, { message: 'Group name is required' });
    minLength(schemaPath.groupName, 2, { message: 'Group name must be at least 2 characters long' });
    required(schemaPath.privacy, { message: 'Privacy is required' });
  });

  private readonly currentUserService = inject(CurrentUserService);
  private readonly recruitGroupService = inject(RecruitGroupService);
  private readonly firebaseService = inject(FirebaseService);
  private readonly toastNotificationService = inject(ToastNotificationService);
  private readonly alertService = inject(AlertService);

  readonly groupList = computed(() => {
    this.membershipRevision();
    const merged = [
      ...(this.recruitGroupService.allRecruitGroups() ?? []),
      ...(this.recruitGroupService.myRecruitGroups() ?? []),
      ...(this.recruitGroupService.brandRecruitGroups() ?? []),
    ];
    const seen = new Set<string>();
    const groups: RecruitGroupResponseDto[] = [];
    for (const group of merged) {
      if (seen.has(group.id) || group.group_type === RecruitGroupTypesEnum.CREATOR_INTEREST) {
        continue;
      }
      seen.add(group.id);
      groups.push(group);
    }
    return groups;
  });

  readonly membershipIds = computed(() => {
    this.membershipRevision();
    return this.creator().recruitGroupIds ?? [];
  });

  async ngOnInit(): Promise<void> {
    const hasCache = this.recruitGroupService.myRecruitGroups() !== undefined
      || this.recruitGroupService.allRecruitGroups() !== undefined;
    this.groupsLoadState.set(hasCache ? 'loaded' : 'loading');
    try {
      await this.recruitGroupService.initRecruitmentGroups();
      this.groupsLoadState.set('loaded');
    } catch {
      if (!hasCache) {
        this.groupsLoadState.set('error');
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  dismiss(): void {
    this.closed.emit();
  }

  showCreateForm(): void {
    this.viewMode.set('addGroup');
  }

  showGroupList(): void {
    this.viewMode.set('view');
  }

  creatorIncludedInGroup(groupId: string): boolean {
    return this.membershipIds().includes(groupId);
  }

  visibilityLabel(group: RecruitGroupResponseDto): string {
    return group.isGlobal || group.businessAccountId?.trim() || group.agencyAccountId?.trim()
      ? 'Visible to team'
      : 'Visible to me';
  }

  async addCreatorToGroup(group: RecruitGroupResponseDto): Promise<void> {
    if (this.updatingGroup()) {
      return;
    }

    if (this.creatorIncludedInGroup(group.id)) {
      this.updatingGroup.set(true);
      const confirm = await this.alertService.alert({
        title: 'Creator already in group',
        message: 'Remove this creator from the group?',
        type: 'confirm',
        icon: 'warning',
        confirmText: 'Remove from group',
        cancelText: 'Cancel',
      });
      if (!confirm) {
        this.updatingGroup.set(false);
        return;
      }
    }

    this.actuallyHandleGroupChange(group.id, group.groupName);
  }

  submitForm(): void {
    this.submitInProgress.set(true);

    const createdGroupName = this.recruitGroupModel().groupName;
    const privacy = this.recruitGroupModel().privacy;
    const newGroup: CreateRecruitGroupDto = {
      groupName: this.recruitGroupModel().groupName,
      groupDescription: this.recruitGroupModel().groupDescription.trim(),
      createdBy: this.firebaseService.userId ?? '',
      isGlobal: privacy === 'global',
      group_type: RecruitGroupTypesEnum.DEFAULT,
    };
    if (privacy === 'brand') {
      newGroup.businessAccountId = this.currentUserService.focussedBrand.id;
    }

    this.subscriptionList.add(
      this.recruitGroupService.createRecruitGroup(newGroup).subscribe({
        next: (response) => {
          this.submitInProgress.set(false);
          this.viewMode.set('view');
          this.recruitGroupModel.set({
            groupName: '',
            groupDescription: '',
            privacy: 'user',
          });
          void this.recruitGroupService.initRecruitmentGroups();
          if (response?.id) {
            this.actuallyHandleGroupChange(response.id, createdGroupName);
          } else {
            void this.toastNotificationService.alert({
              title: 'Group created',
              message: `${createdGroupName} is ready. You can add creators to it now.`,
              type: 'success',
              icon: 'success',
            });
          }
        },
        error: () => {
          this.submitInProgress.set(false);
          void this.toastNotificationService.alert({
            title: 'Failed to create group',
            message: 'Failed to create group',
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }

  private actuallyHandleGroupChange(groupId: string, groupName?: string): void {
    this.updatingGroup.set(true);
    const wasIncluded = this.creatorIncludedInGroup(groupId);
    const creator = this.creator();
    const resolvedGroupName = groupName?.trim() || 'the group';
    const creatorName = creator.firstName?.trim() || 'Creator';

    this.subscriptionList.add(
      this.recruitGroupService.setRecruitmentGroup(groupId, creator.blazeFirebaseUid).pipe(take(1)).subscribe({
        next: () => {
          const ids = creator.recruitGroupIds ?? [];
          creator.recruitGroupIds = wasIncluded
            ? ids.filter((id) => id !== groupId)
            : [...ids, groupId];
          this.membershipRevision.update((count) => count + 1);
          this.recruitGroupService.notifyCreatorGroupMembershipChanged();
          this.updatingGroup.set(false);
          void this.toastNotificationService.alert({
            title: wasIncluded ? 'Removed from group' : 'Added to group',
            message: wasIncluded
              ? `${creatorName} was removed from ${resolvedGroupName}.`
              : `${creatorName} was added to ${resolvedGroupName}.`,
            type: 'success',
            icon: 'success',
            action: wasIncluded
              ? undefined
              : {
                  label: 'Go to Group',
                  routerLink: '/creators-my-groups',
                  queryParams: { group: groupId },
                },
          });
          if (!wasIncluded) {
            this.dismiss();
          }
        },
        error: () => {
          this.updatingGroup.set(false);
          void this.toastNotificationService.alert({
            title: 'Failed to update group',
            message: 'Failed to update group',
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }
}
